import { DateTime } from 'luxon'
import { groupBy } from 'ramda'
import {
  IExpressBusRouterCancelPreviewResponse,
  IExpressBusRouterDetailResponse,
} from '~/api/services/express-bus-router/index.type'
import { DomesticOrderBody } from '~/api/routes/domestic/order/controller/dto'
import { BusMashup, BusWayMashup } from '~/api/routes/domestic/order/controller/type'
import { periodWithDayOrWeekFormat } from '~/api/utils/datetime'
import { toKRW } from '~/api/utils/krNumberToString'
import {
  ICancelPolicy,
  TCancelPolicyType,
} from '~/api/converters/domestic/order/ProductSection/Property/index.type'

type BusDetail = IExpressBusRouterDetailResponse['reservations'][number]

export const BusGradeMap = {
  '1': '우등',
  '2': '일반',
  '3': '심야 우등',
  '4': '심야 일반',
  '7': '프리미엄',
  '8': '심야 프리미엄',
}

const seatTypeSortMap = {
  '1': 1,
  '2': 2,
  '8': 4,
  '9': 3,
}

const seatTypeTextMap = {
  '1': '성인',
  '2': '초등학생',
  '8': '대학생',
  '9': '중고등학생',
}

const busGradeSortInfo = {
  '1': 1,
  '2': 2,
  '8': 4,
  '9': 3,
}

export type TGetBusPriceAndSeatsReturn = {
  price: number
  seats: {
    price: number
    busDiscountAmount: number
    seatNo: number
    ticketType: '1' | '2' | '8' | '9'
  }[]
}

export const getBusPriceAndSeats = ({
  fees,
  departureDate,
  departureTime,
  seats,
}: Pick<
  BusDetail & DomesticOrderBody['bus'][number]['going'],
  'departureDate' | 'departureTime' | 'fees' | 'seats'
>): TGetBusPriceAndSeatsReturn | null => {
  const currentAt = DateTime.now()

  const departure = DateTime.fromISO(`${departureDate}T${departureTime}`).minus({ minute: 5 })
  if (currentAt > departure) {
    return null
  }

  let price = 0

  // 가격 정보가 없는 좌석 정보는 filtering
  const convertedSelectedSeats = seats
    .map(seat => {
      const seatPrice = fees.find(fee => fee.ticketType === seat.ticketType)?.fee ?? 0
      price += seatPrice
      return {
        ...seat,
        price: seatPrice,
        busDiscountAmount: 0, // 오픈 스펙에서는 즉시 할인 제외로 0 고정.
      }
    })
    .filter(selectedSeat => !!selectedSeat.price)

  return {
    price,
    seats: convertedSelectedSeats,
  }
}

export const getExpectedArriveAt = (
  way: Required<Pick<BusDetail, 'departureDate' | 'departureTime' | 'tripDuration'>>
) => {
  const [hours, minutes, seconds] = way.tripDuration.split(':').map(d => +d)
  const [dHours, dMinutes, dSeconds] = way.departureTime.split(':').map(d => +d)
  const departureAt = `${way.departureDate}T00:00:00`
  return DateTime.fromISO(departureAt)
    .plus({
      hours: hours + dHours,
      minutes: minutes + dMinutes,
      seconds: seconds + dSeconds,
    })
    .toFormat('yyyy-MM-dd HH:mm:ss')
}

const compareSeatType = (item1, item2) => seatTypeSortMap[item1[0]] - seatTypeSortMap[item2[0]]

export const getSeatsTypeText = (seats: BusWayMashup['seats']) => {
  const bySeatsType = groupBy((seat: BusWayMashup['seats'][number]) => seat.ticketType)
  return Object.entries(bySeatsType(seats))
    .sort(compareSeatType)
    .map(([key, value]) => `${seatTypeTextMap[key]} ${value.length}`)
    .join(', ')
}

export const getBusTicketName = (bus: BusMashup[number]) => {
  return `[${bus.returns ? '왕복' : '편도'}] ${bus.going.departureTerminalAbbreviationName} - ${
    bus.going.arrivalTerminalAbbreviationName
  }`
}

export const parseBusDateTime = (way: BusMashup[number]['going']) => {
  const [hours, minutes, seconds] = way.tripDuration.split(':').map(d => +d)
  const [dHours, dMinutes, dSeconds] = way.departureTime.split(':').map(d => +d)
  const departureAt = `${way.departureDate}T00:00:00`
  const arrivalAtDateTime = DateTime.fromISO(departureAt).plus({
    hours: hours + dHours,
    minutes: minutes + dMinutes,
    seconds: seconds + dSeconds,
  })
  const arrivalAtDate = arrivalAtDateTime.toFormat('yyyy-MM-dd')
  const arrivalAt = `${arrivalAtDate}T${arrivalAtDateTime.toFormat('HH:mm:ss')}`

  return {
    departureDate: way.departureDate,
    departureTime: way.departureTime,
    arrivalAt,
    arrivalAtDate,
  }
}
export const getBusTicketDurationText = (way: BusMashup[number]['going']) => {
  const { departureDate, departureTime, arrivalAt } = parseBusDateTime(way)
  return periodWithDayOrWeekFormat({
    from: { date: departureDate, time: departureTime },
    to: arrivalAt,
  })
}

export const getBusTicketOptions = (way: BusMashup[number]['going']) => {
  const seats = way.seats.map(seat => seat.seatNo).join(', ')

  return [BusGradeMap[way.busGrade], `${seats}번 좌석`]
}

export const toBusGradeOrder = busGrade => busGradeSortInfo[busGrade]

export const getBusTicketQuantity = (seats: BusMashup[number]['going']['seats']) => {
  const byTicketType = groupBy((seat: BusWayMashup['seats'][number]) => seat.ticketType)
  return Object.entries(byTicketType(seats))
    .sort((item1, item2) => (toBusGradeOrder(item1[0]) > toBusGradeOrder(item2[0]) ? 1 : -1))
    .map(([key, value]) => `${seatTypeTextMap[key]} ${toKRW(value[0].price)} x ${value.length}명`)
}

export const busCancelPolicyHelper = ({
  cancelPreviews,
  currentAt,
}: {
  cancelPreviews: IExpressBusRouterCancelPreviewResponse['buses'][number]['cancelPreviews'] | null
  currentAt: string
}): ICancelPolicy | null => {
  const curDateTime = DateTime.fromISO(currentAt)
  const policy = cancelPreviews?.filter(
    preview => curDateTime < DateTime.fromFormat(preview.endAt, 'yyyy-MM-dd HH:mm:ss')
  )[0]

  return policy
    ? {
        type: policy.type as TCancelPolicyType,
        weight: +(policy?.weight ?? 100),
        endAt: DateTime.fromFormat(policy.endAt, 'yyyy-MM-dd HH:mm:ss').toISO(),
      }
    : null
}
